import type { AgentSessionClient } from "@wags/agent-client";
import { userDeliveryPublicMessageSchema } from "@wags/agent-contracts/user-delivery.contract.ts";
import {
    SEEKER_SCREEN_PART_NAME,
    type SeekerScreenContext,
} from "@wags/agent-contracts/kits-seeker-screen.contract.ts";
import type IKitsChatInteractions from "./kits-chat-interactions.interface";
import {
    wagsAgentMessageExternalSchema,
    wagsAgentToolPartSchema,
    wagsMessageMetadataSchema,
} from "./wags-agent-public-data.contract";
import type {
    WagsMessageMetadata,
    WagsUIMessage,
} from "@/features/kits-conversations";
import { z } from "zod";

type AgentSnapshot = Awaited<ReturnType<AgentSessionClient["snapshot"]>>;
type AsyncItem<T> = T extends AsyncIterable<infer Item> ? Item : never;
type AgentPublicEvent = AsyncItem<ReturnType<AgentSessionClient["subscribe"]>>;
type AgentMessage = AgentSnapshot["messages"][number];
type AgentMessagePart = AgentMessage["parts"][number];
type CompletedMessagePayload = Extract<
    AgentPublicEvent["payload"],
    { type: "message.completed" }
>;
const webAssistantTextMarkerSchema = z
    .object({
        version: z.literal(1),
        firstTextCursor: z.number().int().positive().optional(),
    })
    .strict();
export type WagsAgentChatStatus = "ready" | "submitted" | "streaming" | "error";

export type WagsAgentChatSnapshot = {
    readonly activeTurnId: string | null;
    hydrated: boolean;
    historicalQuickReplyToolCallIds: ReadonlySet<string>;
    messages: WagsUIMessage[];
    status: WagsAgentChatStatus;
    terminalFailure: {
        readonly eventId: string;
        readonly turnId: string | null;
        readonly causationInputId: string | null;
    } | null;
};

function metadataForMessage(message: AgentMessage): WagsMessageMetadata {
    const parsed = wagsAgentMessageExternalSchema.safeParse(
        message.external?.wagsWeb,
    );
    const external = parsed.success ? parsed.data : undefined;
    const metadata = wagsMessageMetadataSchema.safeParse(
        external?.metadata ?? {},
    );
    const visible = { ...(metadata.success ? metadata.data : {}) };
    for (const field of ["userDelivery", "streamPhase", "streamTurnId"])
        Reflect.deleteProperty(visible, field);
    return {
        ...visible,
        canonicalInputId: external?.inputId,
        publicCursor: message.completionCursor,
        interrupted: message.interrupted,
    };
}

function toUiParts(part: AgentMessagePart): WagsUIMessage["parts"] {
    if (part.type === "text") return [{ type: "text", text: part.text }];
    if (part.type === "file") {
        if (typeof part.data !== "string") return [];
        try {
            if (new URL(part.data).protocol !== "https:") return [];
        } catch {
            return [];
        }
        return [
            {
                type: "file",
                filename: part.filename,
                mediaType: part.mediaType,
                url: part.data,
            },
        ];
    }
    return [];
}

function webTextUiMessage(message: AgentMessage): WagsUIMessage | null {
    const marker = webAssistantTextMarkerSchema.safeParse(
        message.external?.webAssistantText,
    );
    if (
        (marker.success || message.visibility === "web") &&
        message.parts.length > 0 &&
        message.parts.every((part) => part.type === "text")
    ) {
        const text = message.parts.map((part) => part.text).join("");
        return {
            id: message.messageId,
            role: "assistant",
            metadata: {
                streamPhase:
                    message.completionCursor === undefined
                        ? "draft"
                        : "committed",
                publicCursor:
                    (marker.success
                        ? marker.data.firstTextCursor
                        : message.firstTextCursor) ?? message.completionCursor,
            },
            parts: [
                {
                    type: "text" as const,
                    text,
                    ...(message.completionCursor === undefined
                        ? { state: "streaming" as const }
                        : {}),
                },
            ],
        };
    }
    return null;
}

export function toPublicUiMessage(message: AgentMessage): WagsUIMessage | null {
    if (message.visibility === "internal") return null;
    if (message.role === "user") {
        return {
            id: message.messageId,
            role: "user",
            metadata: metadataForMessage(message),
            parts: message.parts.flatMap(toUiParts),
        } satisfies WagsUIMessage;
    }
    if (message.role !== "assistant") return null;
    const webText = webTextUiMessage(message);
    if (webText !== null) return webText;
    const parsed = userDeliveryPublicMessageSchema.safeParse(
        message.external?.userDelivery,
    );
    if (!parsed.success) return null;
    const delivery = parsed.data;
    if (
        delivery.deliveryId !== delivery.receipt.deliveryId ||
        delivery.sequence !== delivery.receipt.sequence
    )
        return null;
    const intent = delivery.intent;
    const presentation = delivery.presentation;
    const canonicalWidget = presentation
        ? wagsAgentToolPartSchema.safeParse({
              tool: presentation.widget,
              toolCallId: presentation.toolCallId,
              state: "output-available",
              input: presentation.input,
              output: presentation.output,
          })
        : null;
    const widgetParts: WagsUIMessage["parts"] =
        canonicalWidget?.success &&
        canonicalWidget.data.state === "output-available"
            ? [
                  {
                      type: "dynamic-tool",
                      toolName: canonicalWidget.data.tool,
                      toolCallId: canonicalWidget.data.toolCallId,
                      state: "output-available",
                      input: canonicalWidget.data.input,
                      output: canonicalWidget.data.output,
                  },
              ]
            : [];
    const parts: WagsUIMessage["parts"] =
        intent.kind === "message"
            ? [
                  ...(intent.text
                      ? [{ type: "text" as const, text: intent.text }]
                      : []),
                  ...intent.attachments.map((attachment) => ({
                      type: "file" as const,
                      url: attachment.url,
                      mediaType: attachment.mediaType,
                      filename: attachment.filename,
                  })),
                  ...widgetParts,
              ]
            : intent.kind === "contact"
              ? [
                    {
                        type: "text",
                        text:
                            intent.contact.kind === "self"
                                ? "Contact card"
                                : [
                                      intent.contact.displayName,
                                      intent.contact.organization,
                                      ...intent.contact.phones,
                                      ...intent.contact.emails,
                                  ]
                                      .filter(Boolean)
                                      .join("\n"),
                    },
                ]
              : [
                    {
                        type: "text",
                        text: `${intent.operation === "add" ? "Added" : "Removed"} ${intent.reaction} reaction`,
                    },
                ];
    return {
        id: message.messageId,
        role: "assistant",
        metadata: {
            userDelivery: delivery,
            publicCursor: delivery.createdCursor ?? message.completionCursor,
        },
        parts,
    } satisfies WagsUIMessage;
}

export class WagsAgentChat {
    readonly session: AgentSessionClient;
    private readonly actorId: string;
    private state: WagsAgentChatSnapshot = {
        activeTurnId: null,
        hydrated: false,
        historicalQuickReplyToolCallIds: new Set(),
        messages: [],
        status: "ready",
        terminalFailure: null,
    };
    private capturedInitialQuickReplies = false;
    private readonly subscribers = new Set<() => void>();
    private cursor = 0;
    private abort?: AbortController;
    private watchAcquired = false;
    private watchFailed = false;

    constructor(
        session: AgentSessionClient,
        actorId: string,
        private readonly interactions: Pick<
            IKitsChatInteractions,
            "quickReplyToolCallIds"
        >,
    ) {
        this.session = session;
        this.actorId = actorId;
    }

    getSnapshot = (): WagsAgentChatSnapshot => this.state;

    subscribe = (subscriber: () => void) => {
        this.subscribers.add(subscriber);
        if (!this.watchAcquired) {
            this.watchAcquired = true;
            void this.start().catch(() => undefined);
        }
        return () => {
            this.subscribers.delete(subscriber);
            queueMicrotask(() => {
                if (this.subscribers.size > 0 || !this.watchAcquired) return;
                this.watchAcquired = false;
                this.stop();
            });
        };
    };

    async start(): Promise<void> {
        this.watchFailed = false;
        this.abort?.abort();
        const abort = new AbortController();
        this.abort = abort;
        try {
            const snapshot = await this.session.snapshot(
                {},
                { signal: abort.signal },
            );
            if (abort.signal.aborted) return;
            this.cursor = snapshot.snapshotCursor;
            const activeMessages = snapshot.activeTurn?.messages ?? [];
            const settledMessages = snapshot.messages.flatMap((message) => {
                const visible = toPublicUiMessage(message);
                return visible ? [visible] : [];
            });
            const historicalQuickReplyToolCallIds = this
                .capturedInitialQuickReplies
                ? this.state.historicalQuickReplyToolCallIds
                : this.interactions.quickReplyToolCallIds(settledMessages);
            this.capturedInitialQuickReplies = true;
            this.setState({
                activeTurnId: snapshot.activeTurn?.turnId ?? null,
                hydrated: true,
                historicalQuickReplyToolCallIds,

                messages: this.mergeMessages(
                    [
                        ...settledMessages,
                        ...activeMessages.flatMap((message) => {
                            const uiMessage = toPublicUiMessage(message);
                            return uiMessage
                                ? [
                                      uiMessage.metadata?.streamPhase ===
                                      "draft"
                                          ? {
                                                ...uiMessage,
                                                metadata: {
                                                    ...uiMessage.metadata,
                                                    streamTurnId:
                                                        snapshot.activeTurn
                                                            ?.turnId,
                                                },
                                            }
                                          : uiMessage,
                                  ]
                                : [];
                        }),
                    ],
                    this.state.messages.filter(
                        (message) => message.metadata?.streamPhase !== "draft",
                    ),
                ),
                status: snapshot.activeTurn ? "streaming" : "ready",
                terminalFailure: null,
            });
            for await (const event of this.session.subscribe({
                afterCursor: snapshot.snapshotCursor,
                signal: abort.signal,
            })) {
                if (abort.signal.aborted) return;
                this.applyEvent(event);
            }
        } catch (error) {
            if (!abort.signal.aborted) {
                this.watchFailed = true;
                this.setState({ status: "error" });
                throw error;
            }
        }
    }

    stop(): void {
        this.abort?.abort();
        this.abort = undefined;
    }

    async send(input: {
        inputId: string;
        createdAt: string;
        text: string;
        metadata?: WagsMessageMetadata;
        screen?: SeekerScreenContext;
    }): Promise<void> {
        if (this.watchFailed) void this.start().catch(() => undefined);
        const metadata = {
            ...wagsMessageMetadataSchema.parse(input.metadata ?? {}),
        };
        Reflect.deleteProperty(metadata, "userDelivery");
        Reflect.deleteProperty(metadata, "publicCursor");
        Reflect.deleteProperty(metadata, "streamPhase");
        Reflect.deleteProperty(metadata, "streamTurnId");
        const optimistic = {
            id: `event:${input.inputId}`,
            role: "user",
            metadata: {
                ...metadata,
                canonicalInputId: input.inputId,
                createdAt: input.createdAt,
            },
            parts: [{ type: "text", text: input.text }],
        } satisfies WagsUIMessage;
        this.setState({
            messages: this.mergeMessages([optimistic]),
            status: "submitted",
            terminalFailure: null,
        });
        try {
            await this.session.submit({
                type: "message.submitted",
                inputId: input.inputId,
                createdAt: input.createdAt,
                actor: { actorId: this.actorId, role: "user" },
                content: [
                    { type: "text", text: input.text },
                    ...(input.screen === undefined
                        ? []
                        : [
                              {
                                  type: "data" as const,
                                  name: SEEKER_SCREEN_PART_NAME,
                                  data: input.screen,
                              },
                          ]),
                ],
                response: "automatic",
                external: {
                    wagsWeb: {
                        inputId: input.inputId,
                        metadata,
                    },
                },
            });
        } catch (error) {
            this.setState({
                messages: this.state.messages.filter(
                    (message) => message.id !== optimistic.id,
                ),
                status: "error",
            });
            throw error;
        }
    }

    private applyCompletedMessage(payload: CompletedMessagePayload) {
        const visible = toPublicUiMessage(payload.message);
        if (visible) this.setState({ messages: this.mergeMessages([visible]) });
    }

    private applyFailedTurn(event: AgentPublicEvent): void {
        if (
            this.state.activeTurnId !== null &&
            event.turnId !== this.state.activeTurnId
        ) {
            return;
        }
        this.setState({
            activeTurnId: null,
            messages: this.withoutTurnDrafts(event.turnId),
            status: "error",
            terminalFailure: {
                eventId: event.eventId,
                turnId: event.turnId ?? null,
                causationInputId: event.causationInputId ?? null,
            },
        });
    }

    private withoutTurnDrafts(turnId: string | undefined): WagsUIMessage[] {
        return this.state.messages.filter(
            (message) =>
                message.metadata?.streamPhase !== "draft" ||
                (turnId !== undefined &&
                    message.metadata.streamTurnId !== turnId),
        );
    }

    private applyWebTextDelta(
        event: AgentPublicEvent,
        payload: Extract<
            AgentPublicEvent["payload"],
            { type: "message.delta" }
        >,
    ): void {
        if (event.agentId !== "kits-web") return;
        if (
            event.turnId === undefined ||
            event.turnId !== this.state.activeTurnId
        )
            return;
        const existing = this.state.messages.find(
            (message) => message.id === payload.messageId,
        );
        if (existing?.metadata?.streamPhase === "committed") return;
        const text =
            (existing?.parts
                .filter((part) => part.type === "text")
                .map((part) => part.text)
                .join("") ?? "") + payload.text;
        this.setState({
            messages: this.mergeMessages([
                {
                    id: payload.messageId,
                    role: "assistant",
                    metadata: {
                        streamPhase: "draft",
                        streamTurnId: event.turnId,
                        publicCursor:
                            existing?.metadata?.publicCursor ?? event.cursor,
                    },
                    parts: [{ type: "text", text, state: "streaming" }],
                },
            ]),
            status: "streaming",
        });
    }

    private applyEvent(event: AgentPublicEvent): void {
        const payload = event.payload;

        if (event.cursor <= this.cursor) return;
        this.cursor = event.cursor;
        if (payload.type === "message.completed") {
            this.applyCompletedMessage(payload);
            return;
        }
        if (payload.type === "message.delta") {
            this.applyWebTextDelta(event, payload);
            return;
        }
        if (
            payload.type === "tool.started" ||
            payload.type === "tool.completed" ||
            payload.type === "tool.failed"
        ) {
            this.setState({ status: "streaming" });
            return;
        }
        if (payload.type === "turn.started") {
            this.setState({
                activeTurnId: event.turnId ?? null,
                status: "streaming",
                terminalFailure: null,
            });
            return;
        }
        if (
            payload.type === "turn.completed" ||
            payload.type === "turn.cancelled" ||
            payload.type === "turn.interrupted"
        ) {
            this.setState({
                activeTurnId: null,
                messages: this.withoutTurnDrafts(event.turnId),
                status: "ready",
                terminalFailure: null,
            });
            return;
        }
        if (payload.type === "turn.failed") {
            this.applyFailedTurn(event);
        }
    }

    private mergeMessages(
        incoming: WagsUIMessage[],
        existing = this.state.messages,
    ): WagsUIMessage[] {
        const messages = [...existing];
        for (const message of incoming) {
            const inputId = message.metadata?.canonicalInputId;
            const deliveryId =
                message.role === "assistant"
                    ? message.metadata?.userDelivery?.deliveryId
                    : undefined;
            const index = messages.findIndex(
                (candidate) =>
                    candidate.id === message.id ||
                    (deliveryId !== undefined &&
                        candidate.role === "assistant" &&
                        candidate.metadata?.userDelivery?.deliveryId ===
                            deliveryId) ||
                    (inputId !== undefined &&
                        candidate.metadata?.canonicalInputId === inputId),
            );
            if (index === -1) messages.push(message);
            else messages[index] = message;
        }
        return messages.sort(
            (left, right) =>
                (left.metadata?.publicCursor ?? Number.POSITIVE_INFINITY) -
                (right.metadata?.publicCursor ?? Number.POSITIVE_INFINITY),
        );
    }

    private setState(patch: Partial<WagsAgentChatSnapshot>): void {
        this.state = { ...this.state, ...patch };
        for (const subscriber of this.subscribers) subscriber();
    }
}
